import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  DatasourceInstructionUpdateField,
  DealItemWithVersions,
  ExternalCatalogConnectionCms,
  ProductType,
  PsychologicalPriceStrategy,
  useDeleteDealItemsMutation,
} from '@/lib/graphql/generated/graphql';
import { useComputeChoosePrice } from '@/lib/hooks/useComputeChoosePrice';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useExtractColorFromImages } from '@/lib/hooks/useExtractColorFromImages';
import { useMoveProductsToPosition } from '@/lib/hooks/useMoveProductsToPosition';
import { useShopifyFieldSync } from '@/lib/hooks/useShopifyFieldSync';
import { useSyncImages } from '@/lib/hooks/useSyncImages';
import { useTriggerCategorizeDealItems } from '@/lib/hooks/useTriggerCategorizeDealItems';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { trackEvent } from '@/lib/mixpanel';
import {
  type CguConfig,
  buildCouponTerms as buildCouponTermsUtil,
} from '@/lib/utils/coupon-utils';
import { computeProductGroupKey } from '@/lib/utils/product-grouping';
import { displayModeState } from '@/states/display-mode.state';
import { productsState } from '@/states/products.state';
import {
  selectedProductIdsState,
  selectedProductsCountState,
  selectedProductsState,
  useResetSelection,
} from '@/states/row-selection.state';
import { DisplayMode } from '@/types/display-mode';
import { useAtomValue } from 'jotai';
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  ArrowUpToLine,
  ChevronDown,
  CircleMinus,
  CirclePlus,
  Link,
  ListOrdered,
  Package,
  Pipette,
  SquarePen,
  Trash2,
  Wand2,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { ViewCouponsCgu } from './import-flow/view-coupons-cgu';
import { ViewCouponsInstructions } from './import-flow/view-coupons-instructions';
import { Dialog, DialogContent } from './ui/dialog';
import { SelectPortalContainerContext } from './ui/select';

export interface FloatingActionMenuProps {
  onOpenEditSidebar?: () => void;
}

const FloatingActionMenu: React.FC<FloatingActionMenuProps> = ({
  onOpenEditSidebar,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const user = useUser();
  const {
    deal: { id: dealId, ...deal },
  } = useDealContext();
  const displayMode = useAtomValue(displayModeState);
  const products = useAtomValue(productsState);
  const isOfferLocked = useIsOfferLocked();

  const selectedProductIds = useAtomValue(selectedProductIdsState);
  const selectedProducts = useAtomValue(selectedProductsState);
  const selectedCount = useAtomValue(selectedProductsCountState);

  const hasShopifyConnection =
    (deal.externalCatalogConnections?.filter(
      (connection) =>
        connection.cms === ExternalCatalogConnectionCms.Shopify &&
        connection.isActive
    ).length ?? 0) > 0;

  const hasShopifyPreviewUrl = useMemo(
    () =>
      selectedProducts.some(
        (p) =>
          p.activeVersion.sourceUrl?.includes('shopifypreview.com') ||
          !p.activeVersion.sourceUrl
      ),
    [selectedProducts]
  );

  const { resetSelection } = useResetSelection();
  const { updateProducts } = useUpdateDealItems(dealId);
  const { computeChoosePrice } = useComputeChoosePrice(dealId);
  const { syncImages } = useSyncImages(dealId);
  const { extractColorFromImages, loading: extractingColors } =
    useExtractColorFromImages(dealId);
  const { triggerCategorizeDealItems, loading: categorizingDealItems } =
    useTriggerCategorizeDealItems(dealId);
  const [deleteDealItemsDirectly] = useDeleteDealItemsMutation();
  const { sync: syncShopifyStock, loading: syncingShopify } =
    useShopifyFieldSync(dealId);
  const { sync: syncShopifyUrl, loading: syncingShopifyUrl } =
    useShopifyFieldSync(dealId);
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();
  const { moveProductsToPosition, loading: movingProducts } =
    useMoveProductsToPosition(dealId);

  // Internal state
  const [showMagicWandModal, setShowMagicWandModal] = useState<boolean>(false);
  const [showDeleteItemModal, setShowDeleteItemModal] =
    useState<boolean>(false);
  const [deletingItems, setDeletingItems] = useState<boolean>(false);
  const [showBulkCguModal, setShowBulkCguModal] = useState(false);
  const [cguPortalContainer, setCguPortalContainer] =
    useState<HTMLElement | null>(null);
  const cguDialogContentRefCallback = useCallback(
    (node: HTMLElement | null) => setCguPortalContainer(node),
    []
  );
  const [showBulkUsageStepsModal, setShowBulkUsageStepsModal] = useState(false);
  const [showSyncImagesModal, setShowSyncImagesModal] = useState(false);
  const [showExtractColorModal, setShowExtractColorModal] = useState(false);
  const [showCategorizeModal, setShowCategorizeModal] = useState(false);
  const [showShopifySyncModal, setShowShopifySyncModal] = useState(false);
  const [showShopifyUrlSyncModal, setShowShopifyUrlSyncModal] = useState(false);
  const [bulkUsageStepsView, setBulkUsageStepsView] = useState<
    'instructionsSelect' | 'instructionsBuild' | 'instructionsPreview'
  >('instructionsSelect');

  // States for bulk edit configurations
  const [bulkCgu, setBulkCgu] = useState<CguConfig>({
    audience: { include: true, option: 'all' },
    usage: { include: true, option: 'one' },
    buy: { include: false, option: 'buy_many' },
    perOrder: { include: false, option: 'one_per_order' },
    cumul: { include: true, option: 'non_cumulable' },
    subscription: { include: true, option: 'no_subscription' },
    shipping: { include: false, option: 'shipping_paid' },
    extra: '',
  });

  const [bulkHowToUseTemplate, setBulkHowToUseTemplate] = useState<string[]>([
    t(
      'components.import_flow.coupons_instructions.steps_templates.after_purchase_find_code'
    ),
    t('components.import_flow.coupons_instructions.steps_templates.copy_code'),
    t(
      'components.import_flow.coupons_instructions.steps_templates.step_three_placeholder'
    ),
  ]);

  const [bulkSelectedFlow, setBulkSelectedFlow] = useState<
    'simple' | 'service' | 'custom' | null
  >(null);

  // Computed values
  const isBrand = !user?.isInternalUser;

  // Filter selected products to only include digital products (coupons)
  const digitalSelectedProducts = useMemo(
    () =>
      selectedProducts.filter(
        (product) => product.activeVersion.productType === ProductType.Coupon
      ),
    [selectedProducts]
  );

  const uniqueProductsCount = useMemo(
    () =>
      new Set(selectedProducts.map((p) => p.activeVersion.productGroupKey))
        .size,
    [selectedProducts]
  );

  // Group all products by productGroupKey to detect complete group selection
  const allItemsByGroupKey = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const product of products) {
      const key = product.activeVersion.productGroupKey;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(product.id);
    }
    return map;
  }, [products]);

  // True only when every selected item belongs to a fully-selected group
  const isGroupSelection = useMemo(() => {
    if (selectedProducts.length === 0) return false;
    const selectedIds = new Set(selectedProductIds);
    const selectedGroupKeys = new Set(
      selectedProducts.map((p) => p.activeVersion.productGroupKey)
    );
    for (const groupKey of selectedGroupKeys) {
      const allIdsInGroup = allItemsByGroupKey.get(groupKey) ?? [];
      if (!allIdsInGroup.every((id) => selectedIds.has(id))) return false;
    }
    return true;
  }, [selectedProducts, selectedProductIds, allItemsByGroupKey]);

  // Unique productGroupKeys of the completely-selected groups, sorted by their
  // current position (nulls last). The backend places productGroupKeys[n] at
  // targetPosition + n, so order here determines the final contiguous positions.
  const selectedGroupProductGroupKeys = useMemo(() => {
    const seen = new Map<string, number | null>();
    for (const p of selectedProducts) {
      const key = p.activeVersion.productGroupKey;
      if (!seen.has(key)) seen.set(key, p.activeVersion.position ?? null);
    }
    return [...seen.entries()]
      .sort(([, posA], [, posB]) => {
        if (posA === null && posB === null) return 0;
        if (posA === null) return 1;
        if (posB === null) return -1;
        return posA - posB;
      })
      .map(([key]) => key);
  }, [selectedProducts]);

  // Position extremes for the selected groups (null when no positioned item)
  const minSelectedPosition = useMemo(() => {
    const positions = selectedProducts
      .map((p) => p.activeVersion.position)
      .filter((pos): pos is number => pos !== null && pos !== undefined);
    return positions.length > 0 ? Math.min(...positions) : null;
  }, [selectedProducts]);

  const maxSelectedPosition = useMemo(() => {
    const positions = selectedProducts
      .map((p) => p.activeVersion.position)
      .filter((pos): pos is number => pos !== null && pos !== undefined);
    return positions.length > 0 ? Math.max(...positions) : null;
  }, [selectedProducts]);

  // Maximum position across all products
  const maxExistingPosition = useMemo(() => {
    const positions = products
      .map((p) => p.activeVersion.position)
      .filter((pos): pos is number => pos !== null && pos !== undefined);
    return positions.length > 0 ? Math.max(...positions) : 0;
  }, [products]);

  const handleMoveToTop = async () => {
    await moveProductsToPosition(selectedGroupProductGroupKeys, 1);
    resetSelection();
  };

  const handleMoveUp = async () => {
    if (minSelectedPosition === null || minSelectedPosition <= 1) return;
    await moveProductsToPosition(
      selectedGroupProductGroupKeys,
      minSelectedPosition - 1
    );
    resetSelection();
  };

  const handleMoveDown = async () => {
    if (
      maxSelectedPosition === null ||
      maxSelectedPosition >= maxExistingPosition
    )
      return;
    await moveProductsToPosition(
      selectedGroupProductGroupKeys,
      maxSelectedPosition + 1
    );
    resetSelection();
  };

  const handleMoveToBottom = async () => {
    await moveProductsToPosition(
      selectedGroupProductGroupKeys,
      maxExistingPosition + 1
    );
    resetSelection();
  };

  // Don't show if no products selected
  if (selectedCount === 0) {
    return null;
  }

  // Handle toggle product - executes directly without modal
  const handleToggleProduct = async () => {
    const productsToToggle = products.filter((p) =>
      selectedProductIds.includes(p.id)
    );

    if (productsToToggle.length === 0) return;

    const newIsSelected = displayMode === DisplayMode.DESELECTED;

    const selectedIds = new Set(selectedProductIds);
    await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) => {
          if (!selectedIds.has(p.id)) return p;
          const nextVersion = { ...p.activeVersion, isSelected: newIsSelected };
          return {
            ...p,
            activeVersion: {
              ...nextVersion,
              productGroupKey: computeProductGroupKey(
                nextVersion.name,
                nextVersion.isSelected
              ),
            },
          };
        }),
      mutation: () =>
        updateProducts(
          productsToToggle.map((p) => ({
            id: p.id,
            isSelected: newIsSelected,
          }))
        ),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'handleToggleProduct',
      onSuccessClose: resetSelection,
      // No toast for immediate action
    });
  };

  // Execute sync images after confirmation
  const handleSyncImagesConfirm = async () => {
    setShowSyncImagesModal(false);

    if (selectedProductIds.length === 0) {
      toast({
        title: t('general.error'),
        description: t('general.error_no_product_selected'),
        variant: 'destructive',
      });
      return;
    }

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) => prev,
      mutation: () => syncImages(selectedProductIds),
      checkSuccess: (result) =>
        result.data?.syncImages?.__typename === 'MutationSuccess',
      getErrorMessage: (result) =>
        result.data?.syncImages?.__typename === 'DomainException'
          ? result.data.syncImages.code
          : t('general.error_sync_images'),
      defaultErrorMessage: t('general.error_sync_images'),
      errorContext: 'handleSyncImagesConfirm',
      inProgressMessage: t('general.sync_in_progress'),
      onSuccess: () => {
        toast({
          title: t('general.success'),
          description: t('general.success_sync_images', {
            count: selectedProductIds.length,
          }),
        });
      },
      onSuccessClose: resetSelection,
    });
  };

  // Handle extracting colors from images - opens modal first
  const handleExtractColorClick = () => {
    setShowExtractColorModal(true);
  };

  // Execute extract color after confirmation
  const handleExtractColorConfirm = async () => {
    setShowExtractColorModal(false);

    if (selectedProductIds.length === 0) {
      toast({
        title: t('general.error'),
        description: t('general.error_no_product_selected'),
        variant: 'destructive',
      });
      return;
    }

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) => prev,
      mutation: () => extractColorFromImages(selectedProductIds),
      checkSuccess: (result) =>
        result.data?.extractColorFromImages?.__typename === 'MutationSuccess',
      getErrorMessage: (result) =>
        result.data?.extractColorFromImages?.__typename === 'DomainException'
          ? result.data.extractColorFromImages.code
          : t('general.error_extract_color'),
      defaultErrorMessage: t('general.error_extract_color'),
      errorContext: 'handleExtractColorConfirm',
      inProgressMessage: t('general.extract_color_in_progress'),
      onSuccess: () => {
        toast({
          title: t('general.success'),
          description: t('general.success_extract_color', {
            count: selectedProductIds.length,
          }),
        });
      },
      onSuccessClose: resetSelection,
    });
  };

  // Handle triggering AI categorization - opens modal first
  const handleTriggerCategorizeDealItemsClick = () => {
    setShowCategorizeModal(true);
  };

  // Execute AI categorization after confirmation
  const handleTriggerCategorizeDealItemsConfirm = async () => {
    setShowCategorizeModal(false);

    if (selectedProductIds.length === 0) {
      toast({
        title: t('general.error'),
        description: t('general.error_no_product_selected'),
        variant: 'destructive',
      });
      return;
    }

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) => prev,
      mutation: () => triggerCategorizeDealItems(selectedProductIds, true),
      checkSuccess: (result) =>
        result.data?.triggerCategorizeDealItems?.__typename ===
        'MutationSuccess',
      getErrorMessage: (result) =>
        result.data?.triggerCategorizeDealItems?.__typename ===
        'DomainException'
          ? result.data.triggerCategorizeDealItems.code
          : t('general.error_categorize_deal_items'),
      defaultErrorMessage: t('general.error_categorize_deal_items'),
      errorContext: 'handleTriggerCategorizeDealItemsConfirm',
      inProgressMessage: t('general.categorization_in_progress'),
      onSuccess: () => {
        toast({
          title: t('general.success'),
          description: t('general.success_categorize_deal_items', {
            count: selectedProductIds.length,
          }),
        });
      },
      onSuccessClose: resetSelection,
    });
  };

  const applyPricesWithStrategy = async (
    strategy: PsychologicalPriceStrategy
  ) => {
    setShowMagicWandModal(false);

    if (selectedProductIds.length === 0) return;

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) => prev,
      mutation: () => computeChoosePrice(selectedProductIds, strategy),
      checkSuccess: (result) =>
        result.data?.computeChoosePriceV2?.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.computeChoosePriceV2?.__typename === 'DomainException'
          ? result.data.computeChoosePriceV2.code
          : t('components.bulk_edit_sidebar.apply_psycho_prices_error'),
      defaultErrorMessage: t(
        'components.bulk_edit_sidebar.apply_psycho_prices_error'
      ),
      errorContext: 'applyPricesWithStrategy',
      inProgressMessage: t('general.psycho_prices_in_progress'),
      onSuccess: () => {
        toast({
          title: t('general.success'),
          description: t(
            'components.bulk_edit_sidebar.apply_psycho_prices_success_toast',
            { count: selectedProductIds.length }
          ),
        });
      },
      onSuccessClose: resetSelection,
    });
  };

  // Function to build coupon terms
  const buildCouponTerms = () => {
    return buildCouponTermsUtil({ cgu: bulkCgu });
  };

  // Handle bulk save CGU
  const handleBulkSaveCgu = async (terms: string[]) => {
    const selectedProducts: DealItemWithVersions[] = products.filter(
      (product) => selectedProductIds.includes(product.id)
    );

    if (selectedProducts.length === 0) {
      toast({
        title: t('general.error_no_product_selected'),
        description: t('general.error_please_select_products'),
        variant: 'destructive',
      });
      return;
    }

    const itemsToSave = selectedProducts.map((product) => ({
      id: product.id,
      couponTerms: terms,
    }));

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) =>
          selectedProducts.find((u) => u.id === p.id)
            ? {
                ...p,
                activeVersion: { ...p.activeVersion, couponTerms: terms },
              }
            : p
        ),
      mutation: () => updateProducts(itemsToSave),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_cgu'),
      defaultErrorMessage: t('general.error_cannot_save_cgu'),
      errorContext: 'handleBulkSaveCgu',
      inProgressMessage: t('general.saving_cgu_in_progress'),
      onSuccess: () => {
        setShowBulkCguModal(false);
        toast({
          title: t('general.success'),
          description: t('components.product_view.cgu_updated_products', {
            count: selectedProducts.length,
          }),
        });
      },
      onSuccessClose: resetSelection,
    });
  };

  // Handle bulk save usage steps
  const handleBulkSaveHowToUse = async (steps: string[]) => {
    const selectedProducts: DealItemWithVersions[] = products.filter(
      (product) => selectedProductIds.includes(product.id)
    );

    if (selectedProducts.length === 0) {
      toast({
        title: t('general.error_no_product_selected'),
        description: t('general.error_please_select_products'),
        variant: 'destructive',
      });
      return;
    }

    const itemsToSave = selectedProducts.map((product) => ({
      id: product.id,
      couponHowToUse: steps,
    }));

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) =>
          selectedProducts.find((u) => u.id === p.id)
            ? {
                ...p,
                activeVersion: { ...p.activeVersion, couponHowToUse: steps },
              }
            : p
        ),
      mutation: () => updateProducts(itemsToSave),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_usage_steps'),
      defaultErrorMessage: t('general.error_cannot_save_usage_steps'),
      errorContext: 'handleBulkSaveHowToUse',
      inProgressMessage: t('general.saving_usage_steps_in_progress'),
      onSuccess: () => {
        setShowBulkUsageStepsModal(false);
        toast({
          title: t('general.success'),
          description: t(
            'components.product_view.usage_steps_updated_products',
            {
              count: selectedProducts.length,
            }
          ),
        });
      },
      onSuccessClose: resetSelection,
    });
  };

  const handleDeleteItems = async () => {
    setDeletingItems(true);
    setShowDeleteItemModal(false);

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.filter((p) => !selectedProductIds.includes(p.id)),
      mutation: () =>
        deleteDealItemsDirectly({
          variables: {
            dealId,
            dealItemsIds: selectedProductIds,
            password: 'yolo',
          },
        }),
      checkSuccess: (result) =>
        result.data?.deleteDealItems?.__typename === 'MutationSuccess',
      getErrorMessage: (result) =>
        result.data?.deleteDealItems?.__typename === 'DomainException'
          ? result.data.deleteDealItems.code
          : t('general.error_delete_products'),
      defaultErrorMessage: t('general.error_delete_products'),
      errorContext: 'handleDeleteItems',
      inProgressMessage: t('general.deleting_products_in_progress'),
      onSuccess: () => {
        trackEvent('DEAL_ITEMS_DELETED', {
          dealId,
          itemCount: selectedProductIds.length,
        });
        toast({
          title: t('general.success'),
          description: t(
            'components.deal_items_subscription.toast_items_deleted'
          ),
        });
        setDeletingItems(false);
      },
      onSuccessClose: resetSelection,
      onError: () => {
        setDeletingItems(false);
      },
    });
  };

  // Handle Shopify stock sync - opens modal first
  const handleShopifySyncClick = () => {
    setShowShopifySyncModal(true);
  };

  const handleShopifySyncConfirm = async () => {
    setShowShopifySyncModal(false);
    await syncShopifyStock({
      dealItemsIds: selectedProductIds,
      fields: [DatasourceInstructionUpdateField.StockQuantity],
      onSuccess: resetSelection,
    });
  };

  const handleShopifyUrlSyncClick = () => {
    setShowShopifyUrlSyncModal(true);
  };

  const handleShopifyUrlSyncConfirm = async () => {
    setShowShopifyUrlSyncModal(false);
    await syncShopifyUrl({
      dealItemsIds: selectedProductIds,
      fields: [
        DatasourceInstructionUpdateField.SourceUrl,
        DatasourceInstructionUpdateField.Images,
      ],
      onSuccess: resetSelection,
    });
  };

  return (
    <>
      <AnimatePresence>
        <div className="pointer-events-none fixed inset-0 z-50">
          <div className="absolute bottom-8 left-0 right-0 flex justify-center">
            <motion.div
              className="pointer-events-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex min-w-max items-center gap-4 rounded-lg border border-[#e5e5e5] bg-white p-4 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col whitespace-nowrap">
                  {uniqueProductsCount === selectedCount ? (
                    <p className="text-sm font-semibold text-black">
                      {t('components.bulk_edit_sidebar.products_selected', {
                        count: selectedCount,
                      })}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">
                        {t('components.bulk_edit_sidebar.product_with', {
                          count: uniqueProductsCount,
                        })}
                      </p>
                      <p className="text-sm font-semibold text-black">
                        {t('components.bulk_edit_sidebar.variant_selected', {
                          count: selectedCount,
                        })}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {/* Dropdown "Sélectionner une action" */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="primaryOutline"
                        className="h-10"
                        data-user-guide="Action Dropdown"
                        data-testid="action-dropdown-button"
                      >
                        {t('components.bulk_edit_sidebar.select_action')}
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[16rem]">
                      {/* Prix psychologiques - only for CHOOSE mode */}
                      {!isBrand && (
                        <DropdownMenuItem
                          onClick={() => setShowMagicWandModal(true)}
                          disabled={selectedCount === 0}
                          className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                        >
                          <Wand2 className="mr-2 h-4 w-4" />
                          {t(
                            'components.bulk_edit_sidebar.apply_psycho_prices_button'
                          )}
                        </DropdownMenuItem>
                      )}

                      {/* Extract colors from images */}
                      {!isBrand && (
                        <DropdownMenuItem
                          onClick={handleExtractColorClick}
                          disabled={selectedCount === 0 || extractingColors}
                          className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                        >
                          <Pipette className="mr-2 h-4 w-4" />
                          {t(
                            'components.bulk_edit_sidebar.extract_color_button'
                          )}
                        </DropdownMenuItem>
                      )}

                      {/* AI Categorization */}
                      {!isBrand && (
                        <DropdownMenuItem
                          onClick={handleTriggerCategorizeDealItemsClick}
                          disabled={
                            selectedCount === 0 || categorizingDealItems
                          }
                          className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                        >
                          <Wand2 className="mr-2 h-4 w-4" />
                          {t(
                            'components.bulk_edit_sidebar.apply_ai_categorization_button'
                          )}
                        </DropdownMenuItem>
                      )}

                      {/* External Catalog Sync */}
                      {!isBrand && hasShopifyConnection && (
                        <DropdownMenuItem
                          onClick={handleShopifySyncClick}
                          disabled={selectedCount === 0 || syncingShopify}
                          className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                        >
                          <Package className="mr-2 h-4 w-4" />
                          {t(
                            'components.bulk_edit_sidebar.synchronize_stocks_via_cms'
                          )}
                        </DropdownMenuItem>
                      )}

                      {/* External Catalog URL Sync */}
                      {!isBrand && hasShopifyConnection && (
                        <DropdownMenuItem
                          onClick={handleShopifyUrlSyncClick}
                          disabled={selectedCount === 0 || syncingShopifyUrl}
                          className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                        >
                          <Link className="mr-2 h-4 w-4" />
                          {t(
                            'components.bulk_edit_sidebar.synchronize_url_via_cms'
                          )}
                        </DropdownMenuItem>
                      )}

                      {/* Toggle product selection */}
                      <DropdownMenuItem
                        onClick={handleToggleProduct}
                        disabled={selectedCount === 0}
                        className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                      >
                        {displayMode === DisplayMode.DESELECTED ? (
                          <>
                            <CirclePlus className="mr-2 h-4 w-4" />
                            {t(
                              'components.bulk_edit_sidebar.product_addition_offer'
                            )}
                          </>
                        ) : (
                          <>
                            <CircleMinus className="mr-2 h-4 w-4" />
                            {t(
                              'components.bulk_edit_sidebar.product_removal_offer'
                            )}
                          </>
                        )}
                      </DropdownMenuItem>

                      {/* CGU and Usage Steps for digital products */}
                      {digitalSelectedProducts.length > 0 && (
                        <>
                          <DropdownMenuItem
                            onClick={() => setShowBulkCguModal(true)}
                            disabled={digitalSelectedProducts.length === 0}
                            className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                          >
                            {t('components.bulk_edit_sidebar.config_cgu')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setShowBulkUsageStepsModal(true)}
                            disabled={digitalSelectedProducts.length === 0}
                            className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                          >
                            {t(
                              'components.bulk_edit_sidebar.config_usage_steps'
                            )}
                          </DropdownMenuItem>
                        </>
                      )}

                      {/* Repositioning submenu - only for complete group selections */}
                      {isGroupSelection && !isOfferLocked && (
                        <>
                          <div className="my-1 h-px bg-border" />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="cursor-pointer whitespace-nowrap hover:bg-primary/5">
                              <ListOrdered className="mr-2 h-4 w-4" />
                              {t('kpi.reposition')}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuItem
                                onClick={handleMoveToTop}
                                disabled={
                                  movingProducts || minSelectedPosition === 1
                                }
                                className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                              >
                                <ArrowUpToLine className="mr-2 h-4 w-4" />
                                {t('kpi.move_to_top')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={handleMoveUp}
                                disabled={
                                  movingProducts ||
                                  minSelectedPosition === null ||
                                  minSelectedPosition <= 1
                                }
                                className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                              >
                                <ArrowUp className="mr-2 h-4 w-4" />
                                {t('kpi.move_up')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={handleMoveDown}
                                disabled={
                                  movingProducts ||
                                  maxSelectedPosition === null ||
                                  maxSelectedPosition >= maxExistingPosition
                                }
                                className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                              >
                                <ArrowDown className="mr-2 h-4 w-4" />
                                {t('kpi.move_down')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={handleMoveToBottom}
                                disabled={
                                  movingProducts ||
                                  maxSelectedPosition === maxExistingPosition
                                }
                                className="cursor-pointer whitespace-nowrap hover:bg-primary/5"
                              >
                                <ArrowDownToLine className="mr-2 h-4 w-4" />
                                {t('kpi.move_to_bottom')}
                              </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </>
                      )}

                      {/* Delete products - only for CHOOSE mode */}
                      {!isBrand && (
                        <>
                          <div className="my-1 h-px bg-border" />
                          <DropdownMenuItem
                            onClick={() => setShowDeleteItemModal(true)}
                            disabled={selectedCount === 0}
                            className="cursor-pointer whitespace-nowrap text-red-600 hover:bg-primary/5 focus:bg-red-50 focus:text-red-600"
                            data-testid="delete-products-button"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t(
                              'components.bulk_edit_sidebar.delete_products_button'
                            )}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Button "Modifier les infos" */}
                  <Button
                    data-testid="modify-info-button"
                    onClick={onOpenEditSidebar}
                    className="aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#18181b] px-4 py-2 text-sm font-medium text-[#fafafa] outline-none transition-all hover:bg-[#27272a] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-3 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
                    data-user-guide="Bulk Modification"
                  >
                    <SquarePen className="size-4" />
                    {t('components.bulk_edit_sidebar.modify_info')}
                  </Button>

                  {/* Button "Annuler" */}
                  <Button
                    variant="ghost"
                    onClick={resetSelection}
                    className="aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-[#18181b] outline-none transition-all hover:bg-transparent hover:text-black/70 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-3 dark:hover:bg-accent/50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
                  >
                    {t('general.cancel')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatePresence>

      {/* Modale de prix psychologiques */}
      <AlertDialog
        open={showMagicWandModal}
        onOpenChange={(open) => setShowMagicWandModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                'components.bulk_edit_sidebar.apply_psycho_prices_modal_title'
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'components.bulk_edit_sidebar.apply_psycho_prices_modal_confirmation'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                applyPricesWithStrategy(PsychologicalPriceStrategy.Comma)
              }
            >
              {t(
                'components.bulk_edit_sidebar.apply_psycho_prices_modal_strat_2_cta'
              )}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() =>
                applyPricesWithStrategy(PsychologicalPriceStrategy.Round)
              }
            >
              {t(
                'components.bulk_edit_sidebar.apply_psycho_prices_modal_strat_1_cta'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale de suppression */}
      <AlertDialog
        open={showDeleteItemModal}
        onOpenChange={(open) => setShowDeleteItemModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('components.bulk_edit_sidebar.delete_products_modal_title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'components.bulk_edit_sidebar.delete_products_modal_description',
                {
                  count: selectedCount,
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowDeleteItemModal(false)}
              disabled={deletingItems}
            >
              {t('general.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItems}
              disabled={deletingItems}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-products-modal-confirm-button"
            >
              {deletingItems ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t('general.in_progress')}
                </>
              ) : (
                t('general.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale CGU bulk edit */}
      <Dialog open={showBulkCguModal} onOpenChange={setShowBulkCguModal}>
        <DialogContent
          ref={cguDialogContentRefCallback}
          className="max-h-[90vh] max-w-4xl overflow-y-auto"
        >
          <SelectPortalContainerContext.Provider value={cguPortalContainer}>
            <ViewCouponsCgu
              setImportView={(view) => {
                if (view === 'coupons') {
                  setShowBulkCguModal(false);
                }
              }}
              cgu={bulkCgu}
              setCgu={setBulkCgu}
              buildCouponTerms={buildCouponTerms}
              setCouponTermsTemplate={(terms) => handleBulkSaveCgu(terms)}
            />
          </SelectPortalContainerContext.Provider>
        </DialogContent>
      </Dialog>

      {/* Modale étapes d'utilisation bulk edit */}
      <Dialog
        open={showBulkUsageStepsModal}
        onOpenChange={(open) => {
          setShowBulkUsageStepsModal(open);
          if (!open) {
            setBulkUsageStepsView('instructionsSelect');
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <ViewCouponsInstructions
            setImportView={(view) => {
              if (view === 'coupons') {
                setShowBulkUsageStepsModal(false);
                setBulkUsageStepsView('instructionsSelect');
              } else if (
                view === 'instructionsSelect' ||
                view === 'instructionsBuild' ||
                view === 'instructionsPreview'
              ) {
                setBulkUsageStepsView(view);
              }
            }}
            selectedFlow={bulkSelectedFlow}
            setSelectedFlow={setBulkSelectedFlow}
            howToUseTemplate={bulkHowToUseTemplate}
            setHowToUseTemplate={setBulkHowToUseTemplate}
            couponForm={{ name: '' }}
            onFinish={async () => {
              const filteredSteps = bulkHowToUseTemplate.filter((step) =>
                step.trim()
              );
              await handleBulkSaveHowToUse(filteredSteps);
            }}
            finishing={false}
            importView={bulkUsageStepsView}
          />
        </DialogContent>
      </Dialog>

      {/* Modale de confirmation - Sync Images */}
      <AlertDialog
        open={showSyncImagesModal}
        onOpenChange={(open) => setShowSyncImagesModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('components.bulk_edit_sidebar.apply_sync_images_button')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.bulk_edit_sidebar.confirm_dialog_description', {
                count: selectedCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSyncImagesConfirm}>
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale de confirmation - Extract Color */}
      <AlertDialog
        open={showExtractColorModal}
        onOpenChange={(open) => setShowExtractColorModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('components.bulk_edit_sidebar.extract_color_button')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.bulk_edit_sidebar.confirm_dialog_description', {
                count: selectedCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleExtractColorConfirm}>
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale de confirmation - AI Categorization */}
      <AlertDialog
        open={showCategorizeModal}
        onOpenChange={(open) => setShowCategorizeModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('components.bulk_edit_sidebar.apply_ai_categorization_button')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.bulk_edit_sidebar.confirm_dialog_description', {
                count: selectedCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTriggerCategorizeDealItemsConfirm}
            >
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale de confirmation - Shopify Sync */}
      <AlertDialog
        open={showShopifySyncModal}
        onOpenChange={(open) => setShowShopifySyncModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('components.bulk_edit_sidebar.synchronize_stocks_via_cms')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.bulk_edit_sidebar.confirm_dialog_description', {
                count: selectedCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleShopifySyncConfirm}>
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale de confirmation - Shopify URL Sync */}
      <AlertDialog
        open={showShopifyUrlSyncModal}
        onOpenChange={(open) => setShowShopifyUrlSyncModal(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('components.bulk_edit_sidebar.synchronize_url_via_cms')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.bulk_edit_sidebar.confirm_dialog_description', {
                count: selectedCount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('general.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleShopifyUrlSyncConfirm}>
              {t('general.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FloatingActionMenu;
